import { useEffect, useMemo, useState } from 'react';
import { fetchRegistrationExamVenueOptions } from 'src/app/modules/Recruitment/network/license-exam-crud';
import { useSelector } from 'react-redux';
import {
  selectAgeValidation,
  selectExamModeOptions,
  selectGlobalRegionOptions,
  selectRegionIdTypes,
} from 'src/app/modules/Config/_redux/configSlice';
import { AgeValidation, RegionIdType, SelectOption } from 'src/app/common/types';
import moment from 'moment';

export interface RegionSelectOption extends SelectOption {
  key: string;
  regex: string;
}

export const GENDER_OPTIONS: SelectOption[] = [
  { value: 'M', label: 'recruitment.exam.registration.adminRegistration.registrationInformation.male' },
  { value: 'F', label: 'recruitment.exam.registration.adminRegistration.registrationInformation.female' },
];

interface UseRegistrationInformationParams {
  examType: string;
  examCode: string;
  examineeId: string;
}

export const useRegistrationInformation = ({ examType, examCode, examineeId }: UseRegistrationInformationParams) => {
  const examModeOptions: SelectOption[] = useSelector(selectExamModeOptions);
  const [venueOptionsLoading, setVenueOptionsLoading] = useState(false);
  const [venueOptions, setVenueOptions] = useState<SelectOption[]>([]);
  const idTypeOptions: RegionIdType[] = useSelector(selectRegionIdTypes);
  const regionOptions: RegionSelectOption[] = useSelector(selectGlobalRegionOptions) ?? [];

  const ageValidation: AgeValidation | null = useSelector(selectAgeValidation);
  const birthDateRange = useMemo(
    () => ({
      maxDate: ageValidation?.min ? moment().subtract(ageValidation.min.value, 'years').toDate() : null,
      minDate: ageValidation?.max ? moment().subtract(ageValidation.max.value, 'years').toDate() : null,
    }),
    [ageValidation],
  );

  const loadVenueOptions = async () => {
    if (venueOptionsLoading) {
      return;
    }
    setVenueOptionsLoading(true);
    try {
      const response = await fetchRegistrationExamVenueOptions({
        examType,
        examCode,
        examineeId,
      });
      setVenueOptions(response.map((venue) => ({ value: venue, label: venue })));
    } finally {
      setVenueOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (!examType || !examCode || !examineeId) {
      setVenueOptions([]);
      return;
    }

    loadVenueOptions();
  }, [examType, examCode, examineeId]);

  return {
    loadVenueOptions,
    venueOptionsLoading,
    venueOptions,
    examModeOptions,
    regionOptions,
    idTypeOptions,
    genderOptions: GENDER_OPTIONS,
    birthDateRange,
  };
};
